import { SortOrder } from '@Utils/helpers/sortingHelpers';
import { getTimestampStr } from '@Utils/helpers/stringHelpers';
import axios from 'axios';
import fileDownload from 'js-file-download';
import _isNil from 'lodash/isNil';
import FoldersApi from '@Data/folders/Api';
import { IFolder } from '@Data/folders/Interfaces';
import NotificationsApi from '@Data/notifications/Api';
import {
    NotificationSeverity,
    NotificationType,
} from '@Data/notifications/Interfaces';
import { IUser } from '@Data/users/Interfaces';
import {
    autoQcEndPoints,
    fileEndPoints,
    qualityControlEndpoints,
} from '@Services/data/ApiHandler';
import { GlobalKeys } from '@Services/globalVariables/GlobalKeys';
import {
    addGlobalVariable,
    getGlobalVariable,
    hasGlobalValue,
} from '@Services/globalVariables/GlobalVariableManager';
import { error } from '@Services/notification/NotificationServices';
import store from '@Services/redux/Store';
import { downloadFile, extrapolateData } from './Helpers';
import {
    DownloadFilesSortField,
    FileUploadHeaderType,
    IFileUploadHeader,
    IFileUploadResponse,
    IQcFileResult,
    ISequentialFileStorageObject,
} from './Interfaces';
import LatLngDict from './LatLngDict';
import { FileActions } from './Reducer';
import createFileUploadWorkflow, {
    UploadFileWorkflowAction,
    UploadFileWorkflowState,
} from './UploadFileWorkflow';

const Api = {
    uploadCsv: async (
        selectedFile: File,
        projectId: number,
        successCallback?: () => void,
        failureCallback?: () => void
    ) => {
        const fileData = new FormData();
        fileData.append(
            'title',
            selectedFile.name.length > 30
                ? selectedFile.name.substring(0, 29)
                : selectedFile.name
        );
        fileData.append('document', selectedFile);
        fileData.append('project_id', projectId.toString());
        fileData.append('upload_type', 'layer');

        const response = await fileEndPoints.uploadFile(fileData);

        if (response.isSuccess) {
            const data: IFileUploadResponse = response.data;
            const fileHeaderState = {
                fileId: data.id,
                fileHeaders: data.fieldList.map((value, i) => {
                    const header: IFileUploadHeader = {
                        value: value,
                        index: i,
                    };
                    return header;
                }),
            };
            store.dispatch(FileActions.setFileHeadersState(fileHeaderState));

            Api.getNewWorkflowEngineState(
                UploadFileWorkflowState.SUBMITTING_HEADERS,
                UploadFileWorkflowAction.SUBMITTED_HEADERS
            );

            if (successCallback) {
                successCallback();
            }
        } else if (failureCallback) {
            failureCallback();
        }
    },
    resetState: (callback?: () => void) => {
        store.dispatch(FileActions.resetState());
        if (callback) {
            callback();
        }
    },
    trySetLatLng: (callback?: () => void) => {
        const headers = store.getState().files.fileHeaders;
        if (headers.length > 0) {
            const latField = LatLngDict.getDistinctField(
                headers,
                FileUploadHeaderType.LATITUDE
            );
            if (!_isNil(latField)) {
                store.dispatch(FileActions.setLatField(latField));
            }

            const lngField = LatLngDict.getDistinctField(
                headers,
                FileUploadHeaderType.LONGITUDE
            );
            if (!_isNil(lngField)) {
                store.dispatch(FileActions.setLngField(lngField));
            }
        }

        if (callback) {
            callback();
        }
    },
    selectField: (
        field: IFileUploadHeader,
        fieldType: FileUploadHeaderType,
        callback?: () => void
    ) => {
        switch (fieldType) {
            case FileUploadHeaderType.LATITUDE:
                store.dispatch(FileActions.setLatField(field));
                break;
            case FileUploadHeaderType.LONGITUDE:
                store.dispatch(FileActions.setLngField(field));
                break;
            case FileUploadHeaderType.POLE_ID:
                store.dispatch(FileActions.setPoleIdField(field));
                break;
        }
        if (callback) {
            callback();
        }
    },
    submitHeaders: async (
        latField: IFileUploadHeader,
        lngField: IFileUploadHeader,
        poleIdField: IFileUploadHeader,
        fileId: string,
        folderName: string,
        successCallback?: (response: any) => void,
        failureCallback?: () => void
    ) => {
        const fileData = new FormData();
        fileData.append('idField', poleIdField.value);
        fileData.append('latField', latField.value);
        fileData.append('lngField', lngField.value);
        fileData.append('rawId', fileId);
        fileData.append('title', folderName);

        const response = await fileEndPoints.createEntitiesFromFile(fileData);

        if (response.isSuccess) {
            if (successCallback) {
                successCallback(response.data.taskId);
            }
        } else if (failureCallback) {
            failureCallback();
        }
    },
    downloadProjectFile: async (
        selectedFolder: IFolder,
        callback?: () => void
    ) => {
        const response = await axios.get(
            `/geoserver/koru/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=koru%3AdownloadDatabyFolder&VIEWPARAMS=folder_id:${selectedFolder.folder_id}&outputFormat=application/json`
        );

        if (response.status === 200) {
            const extrapolatedData = extrapolateData(response.data);

            downloadFile(selectedFolder.folder_name, 'csv', extrapolatedData);

            if (callback) {
                callback();
            }
        }
    },
    downloadFileFromUrl: async (
        reportUrl: string,
        fileName: string,
        callback?: () => void
    ) => {
        const response = await fileEndPoints.downloadFileFromUrl(reportUrl);
        if (response.isSuccess) {
            fileDownload(response.data, fileName);
            if (callback) {
                callback();
            }
        }
    },
    getNewWorkflowEngineState: (
        currentState: string,
        action?: UploadFileWorkflowAction,
        params?: any
    ) => {
        const workflow = createFileUploadWorkflow(currentState);

        if (!_isNil(action)) {
            workflow[action](params);
        }

        store.dispatch(FileActions.setWorkflowEngineState(workflow.state));
    },
    createFolderAndUploadFiles: async (
        files: Array<File>,
        projectId: number,
        callback?: () => void
    ) => {
        FoldersApi.createFolder(
            `ImageUploads_${getTimestampStr()}`,
            projectId,
            (folderId) => {
                // add files to global variable
                const notificationId = `${projectId}Files-${Date.now()}`;

                const storageObj: ISequentialFileStorageObject = {
                    folderId: folderId,
                    projectId: projectId,
                    files: files,
                };

                const globalFilesObj = hasGlobalValue(
                    GlobalKeys.SequentialFiles
                )
                    ? getGlobalVariable(GlobalKeys.SequentialFiles)
                    : {};

                globalFilesObj[notificationId] = storageObj;

                addGlobalVariable(
                    GlobalKeys.SequentialFiles,
                    globalFilesObj,
                    async () => {
                        if (callback) {
                            callback();
                        }
                        // create a new sequential notification
                        NotificationsApi.addNotification(
                            'Multiple image upload',
                            NotificationSeverity.INFO,
                            NotificationType.SEQUENTIAL,
                            notificationId,
                            { total: files.length }
                        );

                        await Api.processSequentialFile(notificationId);
                    }
                );
            }
        );
    },
    processSequentialFile: async (notificationId: string) => {
        // get obj from storage
        if (hasGlobalValue(GlobalKeys.SequentialFiles)) {
            const globalObj: ISequentialFileStorageObject = getGlobalVariable(
                GlobalKeys.SequentialFiles
            );
            const fileStorageObj: ISequentialFileStorageObject =
                globalObj[notificationId];

            if (!_isNil(fileStorageObj)) {
                // shift 1st in queue, upload it
                const firstFile = fileStorageObj.files.shift();
                const formData = new FormData();
                formData.append(
                    'project_id',
                    fileStorageObj.projectId.toString()
                );
                formData.append('folder_id', fileStorageObj.folderId);
                formData.append('upload_type', 'koru_process');
                formData.append('document', firstFile);

                const response = await fileEndPoints.uploadSingleFileToFolder(
                    formData
                );

                const onUpload = () => {
                    // update notification on completion
                    NotificationsApi.updateSequentialNotification(
                        notificationId,
                        fileStorageObj.files.length
                    );

                    // try to upload the next file
                    if (fileStorageObj.files.length > 0) {
                        globalObj[notificationId] = fileStorageObj;
                        addGlobalVariable(
                            GlobalKeys.SequentialFiles,
                            globalObj
                        );
                        return Api.processSequentialFile(notificationId);
                    } else {
                        FoldersApi.getFolders(fileStorageObj.projectId);
                        delete globalObj[notificationId];
                        addGlobalVariable(
                            GlobalKeys.SequentialFiles,
                            globalObj
                        );
                    }
                };

                if (response.isSuccess) {
                    onUpload();
                } else {
                    onUpload();
                    // throw error toast
                    error(`File upload for ${firstFile.name} failed`);
                }
            }
        }
    },
    setTableSorting: (field: DownloadFilesSortField, order: SortOrder) => {
        store.dispatch(
            FileActions.setTableSorting({ field: field, order: order })
        );
    },
    getQcResults: async (
        folderId: string,
        users?: Array<IUser>,
        callback?: () => void
    ) => {
        const response = await qualityControlEndpoints.getQcResults(folderId);
        if (response.isSuccess) {
            const files: Array<IQcFileResult> = response.data.data.map(
                (qcResult) => {
                    return {
                        ...qcResult,
                        userName: '', //users.find(u => u.user_id === qcResult.QC_run_user_id).username
                    };
                }
            );

            store.dispatch(FileActions.setUploadedQcFiles(files));
            if (callback) {
                callback();
            }
        }
    },

    getAutoQcMasterCatalogue: async (
        fileName: string,
        callback?: () => void
    ) => {
        const response = await autoQcEndPoints.getAutoQcMasterCatalogue();
        if (response.isSuccess) {
            const masterCatalogue = JSON.stringify(response.data.data, null, 2);
            fileDownload(masterCatalogue, fileName);
            if (callback) {
                callback();
            }
        }
    },

    getProjectAutoQcConfig: async (
        projectId: number,
        fileName: string,
        callback?: () => void
    ) => {
        const response = await autoQcEndPoints.getProjectAutoQcConfig(
            projectId
        );
        if (response.isSuccess) {
            const projectConfig = JSON.stringify(response.data, null, 2);
            fileDownload(projectConfig, fileName);
            if (callback) {
                callback();
            }
        }
    },

    updateProjectAutoQcConfig: async (
        projectId: number,
        config: object,
        callback?: () => void
    ) => {
        const response = await autoQcEndPoints.updateProjectAutoQcConfig(
            projectId,
            config
        );
        if (response.isSuccess) {
            console.log(`Updated config for project ${projectId}`);

            if (callback) {
                callback();
            }
        }
    },
};

export default Api;
